import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { connect, useDispatch, useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import { CurrentItemBreadcrumbs } from '../CurrentItemBreadcrumbs';
import { PromotionButtonsSection } from '../PromotionButtonsGroupSection';
import { BottomInfoSection } from '../BottomInfoSection';
import { OperationButton } from '../OperationButton';
import { useFullPhoneNumbers } from '../../../../hooks/useFullPhoneNumbers';
import { getItemTitle } from '../../../../utils/helpers/getCurrentItemDescriptionTitle';
import { ErrorPage } from '../../../ui/ErrorPage';
import { getCardTime } from '../../../../utils/helpers';
import { ItemPhotosGallery } from '../../ItemPhotosGallery';
import { formatToBakuTime } from '../../../../utils/helpers/common';
import { TIME_FORMAT, urlHashes } from '../../../../utils/helpers/constants';
import useScreenSize from '../../../../hooks/useScreenSize';
import { CurrentItemTitle } from '../CurrentItemTitle';
import { CurrentItemContentWrapper } from '../../CurrentItemLayout/styled';
import { CurrentItemSectionsProps } from './types';
import { PriceSizeBlock } from '../blocks/PriceSizeBlock';
import { OwnerContactsBlock } from '../blocks/OwnerContactsBlock';
import { AuthorManagementBlock } from '../blocks/AuthorManagementBlock';
import { LocationDescriptionBlock } from '../blocks/LocationDescriptionBlock';
import {
  CurrentItemColumnLayout,
  HiddenOnDesktopBlock,
  LeftColumn,
  operationButtonStyles,
  PriceAndContactsBlockWrapper,
  ReportSectionWrapper,
  RightColumn,
  RightColumnPlaceholder,
  RightColumnWrapper
} from './styled';
import { SpaceSection } from '../../SpaceSection';
import { InteractionTargetTypeEnum } from '../../../../graphql/types';
import { AuthIframe } from '../../../AuthIframe';
import { selectCurrentItemAuthModalOpen } from '../../../../store/selectors';
import { setCurrentItemAuthModalOpen, setError } from '../../../../actions';
import { useStickyHeader } from '../../../../hooks/useStickyHeader';
import { useClient } from '../../../../hooks/useClient';
import { MessageBlock } from '../blocks/MessageBlock';
import { AdDuplicateCard } from '../../../Cards/AdDuplicateCard';
import { createAdriverCustom } from '../../../../utils/helpers/createAdriverCustom';
import { AdriverCustom } from '../../../../types';
import { normalizeTargetingParameter } from '../../../../utils/helpers/normalizeTargetingParameter';
import { transliterateCyrillicToLatin } from '../../../../utils/helpers/transliterateCyrillicToLatin';
import { ReloadPageAlert } from '../ReloadPageAlert';

const ReportSection = dynamic(
  () => import('../ReportSection').then(mod => mod.ReportSection),
  {
    ssr: false
  }
);
const BottomPhoneButton = dynamic(
  () => import('../../BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false
  }
);

const mapDispatchToProps = dispatch => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

const CurrentItemSectionsComponent: React.FC<CurrentItemSectionsProps> = ({
  data,
  item,
  maskedPhones,
  isEditAvailable,
  isRemoveAvailable,
  isAuthor,
  isResidence,
  isAgency,
  isPublished,
  isLimited,
  isExpired,
  isRejected,
  isPending,
  isExpiredManually,
  isLeased,
  paidServices,
  itemPath,
  prettyPrice,
  prettyArea,
  prettyAreaWithUnits,
  prettyLandAreaWithUnits,
  getPriceCurrency,
  onOpenRestoreViewHandler,
  onOpenLimitPaymentViewHandler,
  isFavorited,
  setFavorited,
  setErrorUI,
  userRelatedItemLoading
}) => {
  const router = useRouter();
  const { id } = router.query;
  const { t, i18n } = useTranslation();
  const { isWebView } = useBridgeState();
  const { isSmall, isSmallOrLargeDesktop } = useScreenSize();
  const isAuthModalOpen = useSelector(selectCurrentItemAuthModalOpen);
  const dispatch = useDispatch();
  const isClient = useClient();

  const [isAdReported, setIsAdReported] = useState(false);
  const [hasAuthHash, setHasAuthHash] = useState(false);
  const { isFixed } = useStickyHeader({ isAdPage: true });

  const {
    onClickHandler,
    phonesShown,
    setPhonesShown,
    setIsVisible,
    isVisible,
    showAlertMessage
  } = useFullPhoneNumbers({
    id,
    residenceId: null,
    path: 'items',
    targetType: InteractionTargetTypeEnum.Item,
    agencyId: null,
    setErrorUI
  });

  const itemTitleData = {
    lease: item?.isLeased,
    area: prettyArea,
    rooms: item?.rooms,
    areaCurrency: item?.area?.units,
    category: item?.category?.name,
    location: item?.location
  };

  const currentItemDescTitle = getItemTitle({ ...itemTitleData });
  const galleryModalDescription = getItemTitle({
    ...itemTitleData,
    isClearString: true
  });

  const cityName = item?.city?.name || '';
  const cityId = item?.city?.id || '';
  const adriverCityName =
    i18n.language === 'az'
      ? normalizeTargetingParameter(cityName)
      : transliterateCyrillicToLatin(cityId, cityName);

  const adriverCustom = useMemo<AdriverCustom>(
    () =>
      createAdriverCustom({
        leased: item?.isLeased?.toString(),
        rooms: item?.rooms ?? undefined,
        categoryId: item?.category?.id,
        hasRepair: item?.hasRepair?.toString() as 'true' | 'false' | undefined,
        cityName: adriverCityName ?? undefined,
        price: item?.price?.total ?? undefined,
        hasBillOfSale: item?.hasBillOfSale ?? undefined,
        hasMortgage: item?.hasMortgage ?? undefined
      }),
    [
      item?.isLeased,
      item?.rooms,
      item?.category?.id,
      item?.hasRepair,
      adriverCityName,
      item?.city?.id,
      i18n.language,
      item?.price?.total,
      item?.hasBillOfSale,
      item?.hasMortgage
    ]
  );

  useEffect(() => {
    setFavorited(!!item?.isBookmarked);
  }, [item?.isBookmarked]);

  useEffect(() => {
    if (phonesShown) setPhonesShown(null);
  }, [id]);

  useEffect(() => {
    setIsAdReported(!!item?.isReported);
  }, [item?.isReported]);

  useEffect(() => {
    setHasAuthHash(router.asPath.includes(urlHashes.authentication));
  }, [router.asPath]);

  const operationButtonText = isExpired
    ? t('pages.current_item.buttons.restore')
    : t('pages.current_item.buttons.pay');

  const handleOperationButtonClick = isExpired
    ? onOpenRestoreViewHandler
    : onOpenLimitPaymentViewHandler;

  const expiresAt =
    item?.expiresAt &&
    formatToBakuTime(item?.expiresAt, i18n, TIME_FORMAT.numDateTime);

  const shouldShowMessageBlock =
    (isAuthor && !isPublished) || (!isAuthor && isExpired);

  const messageBlock = shouldShowMessageBlock ? (
    <MessageBlock
      rejectReason={item?.rejectReason?.message}
      isAuthor={isAuthor}
      isPublished={isPublished}
      isLeased={isLeased}
      isExpiredManually={isExpiredManually}
      isPending={isPending}
      isRejected={isRejected}
      isLimited={isLimited}
      isExpired={isExpired}
      expiresAt={expiresAt}
    />
  ) : null;

  const duplicateCard = item?.rejectReason?.duplicateOf ? (
    <AdDuplicateCard duplicateOf={item.rejectReason.duplicateOf} />
  ) : null;

  const operationButton = (
    <OperationButton
      text={operationButtonText}
      onClickHandler={handleOperationButtonClick}
      styles={operationButtonStyles}
    />
  );

  const showOnTabledOrMobile = !isClient || !isSmallOrLargeDesktop;

  const priceSizeBlock = (
    <PriceSizeBlock
      item={item}
      priceCurrency={getPriceCurrency() || ''}
      prettyAreaWithUnits={prettyAreaWithUnits}
      prettyLandAreaWithUnits={prettyLandAreaWithUnits}
      isAuthor={isAuthor}
      isExpired={isExpired}
      operationButton={
        isAuthor &&
        (isExpired || isLimited) &&
        showOnTabledOrMobile &&
        operationButton
      }
      userRelatedItemLoading={userRelatedItemLoading}
    />
  );

  const shouldShowAuthManagementBlock =
    isPublished || (!isLimited && !isRejected);

  const authorManagement = shouldShowAuthManagementBlock ? (
    <AuthorManagementBlock
      isAuthor={isAuthor}
      isEditAvailable={isEditAvailable}
      isRemoveAvailable={isRemoveAvailable}
      {...{
        itemPath,
        paidServices,
        isPublished,
        isLimited,
        isRejected,
        expiresAt
      }}
    />
  ) : null;

  const contactsBlock = (
    <OwnerContactsBlock
      item={item}
      isAuthor={isAuthor}
      {...{
        isPublished,
        isAgency,
        isResidence,
        maskedPhones,
        onClickHandler,
        phonesShown,
        isVisible,
        setIsVisible
      }}
    />
  );

  const notAuthorPromotion = (
    <PromotionButtonsSection isAuthor={isAuthor} paidServices={paidServices} />
  );

  const bottomInfoUpdatedAt = getCardTime(
    item?.updatedAt,
    i18n,
    TIME_FORMAT.numDateTime
  );

  const bottomInfoExpiresAt = formatToBakuTime(
    item?.expiresAt,
    i18n,
    TIME_FORMAT.numDateTime
  );

  const handleCloseAuthModal = () => {
    dispatch(setCurrentItemAuthModalOpen(false));
  };

  if (!data) {
    return (
      <ErrorPage
        title={t('pages.current_item.not_found.title')}
        message={t('pages.current_item.not_found.description')}
        buttonName={t('pages.current_item.buttons.go_to_home')}
        onClick={() => router.push('/')}
      />
    );
  }

  return (
    <>
      <div {...(!hasAuthHash ? { 'data-lotriver-header': '' } : {})} />
      {!isWebView && (
        <CurrentItemBreadcrumbs
          breadcrumbs={item?.breadcrumbs}
          title={galleryModalDescription}
        />
      )}

      {(!isClient || !isSmall) && (
        <CurrentItemTitle
          title={currentItemDescTitle}
          isReportButtonShown={!isAuthor && !isExpired}
          isBookmarkedButtonShow={isPublished}
          itemId={item?.id || ''}
          isReported={isAdReported}
          isFavorited={isFavorited}
          setFavorited={setFavorited}
          setIsAdReported={setIsAdReported}
          userRelatedItemLoading={userRelatedItemLoading}
          hideOnMobile
        />
      )}

      <CurrentItemColumnLayout>
        <LeftColumn>
          <ItemPhotosGallery
            vipped={!!item?.isVipped}
            featured={!!item?.isFeatured}
            photos={item?.photos}
            price={`${prettyPrice} ${item?.price?.currency}`}
            description={galleryModalDescription}
            isBookmarked={isFavorited}
            setFavorited={setFavorited}
            isShowBookmark={isPublished}
            setClickOnGallery={onClickHandler}
            title={currentItemDescTitle}
            itemId={item?.id || ''}
            isReported={isAdReported}
            phones={phonesShown}
            isExpired={isExpired}
            maskedPhones={maskedPhones}
            setIsAdReported={setIsAdReported}
            isLeased={isLeased}
            isAuthor={isAuthor}
            isExpiredManually={isExpiredManually}
            adriverCustom={adriverCustom}
            setIsVisible={setIsVisible}
            isVisible={isVisible}
          />

          {showOnTabledOrMobile && (
            <>
              <HiddenOnDesktopBlock>{messageBlock}</HiddenOnDesktopBlock>
              <HiddenOnDesktopBlock>{duplicateCard}</HiddenOnDesktopBlock>
            </>
          )}

          <CurrentItemContentWrapper>
            {showOnTabledOrMobile && (
              <HiddenOnDesktopBlock>{priceSizeBlock}</HiddenOnDesktopBlock>
            )}

            {isAuthor && showOnTabledOrMobile && (
              <HiddenOnDesktopBlock>{authorManagement}</HiddenOnDesktopBlock>
            )}

            <LocationDescriptionBlock
              item={item}
              currentItemDescTitle={currentItemDescTitle}
              isLeased={isLeased}
            />

            {(isPublished || isAuthor) && showOnTabledOrMobile && (
              <HiddenOnDesktopBlock>{contactsBlock}</HiddenOnDesktopBlock>
            )}

            <SpaceSection>
              <BottomInfoSection
                isAuthor={isAuthor}
                isExpired={isExpired}
                id={item?.id || ''}
                views={item?.views || 0}
                isExpiredManually={isExpiredManually}
                updatedAt={bottomInfoUpdatedAt}
                expiresAt={bottomInfoExpiresAt}
              />
              {!isAuthor && showOnTabledOrMobile && (
                <HiddenOnDesktopBlock>
                  {notAuthorPromotion}
                </HiddenOnDesktopBlock>
              )}
            </SpaceSection>

            <ReportSectionWrapper $smallVerticalSpace>
              {!isAuthor && !isExpired && isSmall && (
                <ReportSection
                  itemId={item?.id || ''}
                  isReported={!!item?.isReported}
                />
              )}
              {!isAuthor && !isExpired && !isSmallOrLargeDesktop && (
                <BottomPhoneButton
                  stat="product-call-btn"
                  phones={phonesShown}
                  clickHandler={onClickHandler}
                  {...{ setIsVisible, isVisible, isAuthor }}
                />
              )}
            </ReportSectionWrapper>
          </CurrentItemContentWrapper>
        </LeftColumn>

        {((isSmallOrLargeDesktop && isClient) || !isClient) && (
          <RightColumnWrapper>
            <RightColumn $isHeaderFixed={isFixed}>
              {messageBlock}
              {isAuthor && (isExpired || isLimited) && operationButton}
              {userRelatedItemLoading && (
                <RightColumnPlaceholder
                  height="463px"
                  margin="0"
                  borderRadius="14px"
                />
              )}
              {duplicateCard}
              {isAuthor && !userRelatedItemLoading && authorManagement}
              <PriceAndContactsBlockWrapper>
                {priceSizeBlock}
                {(isPublished || isAuthor) && contactsBlock}
              </PriceAndContactsBlockWrapper>
              {!isAuthor && notAuthorPromotion}
            </RightColumn>
          </RightColumnWrapper>
        )}
      </CurrentItemColumnLayout>
      {showAlertMessage && <ReloadPageAlert />}
      {isAuthModalOpen && <AuthIframe closeHandler={handleCloseAuthModal} />}
    </>
  );
};

export const CurrentItemSections = connect(
  null,
  mapDispatchToProps
)(CurrentItemSectionsComponent);
